<!-- EnhancedSelect.vue - 大数据量时显示前100条 -->
<template>
  <el-select 
    v-model="selectedValue"
    :class="className"
    :placeholder="placeholder"
    :disabled="disabled"
    :clearable="clearable"
    filterable
    :filter-method="handleFilter"
    @visible-change="handleVisibleChange"
    @change="handleChange"
    @clear="handleClear"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <el-option 
      v-for="(item, index) in displayOptions" 
      :key="`${item[valueKey]}_${index}`" 
      :label="item[labelKey]"
      :value="item[valueKey]"
    >
    </el-option>
    
    <!-- 数据量过多提示 -->
    <div v-if="showDataTip && !currentQuery" slot="empty" class="data-tip">
      共 {{ allOptions.length }} 条数据，请输入关键词搜索
    </div>
    
    <!-- 无数据提示 -->
    <div v-else-if="displayOptions.length === 0 && currentQuery" slot="empty" class="search-empty">
      未找到匹配的数据
    </div>
  </el-select>
</template>

<script>
export default {
  name: 'EnhancedSelect',
  
  props: {
    // v-model 绑定的值
    value: {
      type: [String, Number, Array],
      default: ''
    },
    
    // 选项数据源
    options: {
      type: Array,
      default: () => []
    },
    
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    
    // 占位符
    placeholder: {
      type: String,
      default: '请选择'
    },
    
    // 是否可清空
    clearable: {
      type: Boolean,
      default: true
    },
    
    // 自定义类名
    className: {
      type: String,
      default: 'public_optionSty dropDown_Define'
    },
    
    // 显示的文本字段名
    labelKey: {
      type: String,
      default: 'SETTB99CA00'
    },
    
    // 值字段名
    valueKey: {
      type: String,
      default: 'SETTB99ID'
    },
    
    // 拼音字段名（用于搜索）
    pinyinKey: {
      type: String,
      default: 'SETTB99CA00PY'
    },
    
    // 是否启用拼音搜索
    enablePinyinSearch: {
      type: Boolean,
      default: true
    },
    
    // 是否启用首字母搜索
    enableFirstLetterSearch: {
      type: Boolean,
      default: false
    },
    
    // 大数据量阈值（超过此数量时只显示前N条）
    largeDataThreshold: {
      type: Number,
      default: 500
    },
    
    // 大数据量时初始显示数量
    initDisplayCount: {
      type: Number,
      default: 100
    }
  },
  
  data() {
    return {
      displayOptions: [],      // 显示的选项
      searchTimer: null,       // 防抖定时器
      currentQuery: '',        // 当前搜索词
      allOptions: [],          // 全部数据
      searchIndex: null,       // 搜索索引（性能优化）
      showDataTip: false       // 是否显示数据提示
    }
  },
  
  computed: {
    selectedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    
    // 是否为大数据量
    isLargeData() {
      return this.allOptions.length > this.largeDataThreshold;
    }
  },
  
  watch: {
    options: {
      handler(newVal) {
        const startTime = performance.now();
        this.allOptions = [...newVal];
        // 构建搜索索引
        this.buildSearchIndex();
        // 初始化显示数据
        this.initDisplayData();
        const endTime = performance.now();
      },
      immediate: true,
      deep: true
    },
    
    // 监听外部清空值
    value(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.resetToAllData();
      }
    }
  },
  
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
  },
  
  methods: {
    /**
     * 构建搜索索引（一次性预处理，搜索时直接用）
     */
    buildSearchIndex() {
      if (!this.allOptions || this.allOptions.length === 0) {
        this.searchIndex = null;
        return;
      }
      
      // 数据量小于500时不需要索引，直接搜索更快
      if (this.allOptions.length < 500) {
        this.searchIndex = null;
        return;
      }
      
      const startTime = performance.now();
      
      this.searchIndex = this.allOptions.map((item, index) => {
        const label = (item[this.labelKey] || '').toLowerCase();
        const pinyin = (item[this.pinyinKey] || '').toLowerCase();
        
        // 预计算拼音首字母（如果启用）
        let firstLetters = '';
        if (this.enableFirstLetterSearch && pinyin) {
          const words = pinyin.split(' ');
          const letters = [];
          for (let w = 0; w < words.length; w++) {
            if (words[w]) {
              letters.push(words[w].charAt(0));
            }
          }
          firstLetters = letters.join('').toLowerCase();
        }
        
        return {
          index: index,
          label: label,
          pinyin: pinyin,
          firstLetters: firstLetters,
          item: item
        };
      });
      
      const endTime = performance.now();
    },
    
    /**
     * 初始化显示数据（大数据量时只显示前100条）
     */
    initDisplayData() {
      if (!this.allOptions || this.allOptions.length === 0) {
        this.displayOptions = [];
        this.showDataTip = false;
        return;
      }
      
      // 大数据量时只显示前 N 条
      if (this.isLargeData) {
        this.displayOptions = this.allOptions.slice(0, this.initDisplayCount);
        this.showDataTip = true;
        
      } else {
        this.displayOptions = [...this.allOptions];
        this.showDataTip = false;
      }
    },
    
    /**
     * 重置显示全部数据（大数据量时显示前100条）
     */
    resetToAllData() {
      this.currentQuery = '';
      this.showDataTip = false;
      
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      
      // 大数据量时显示前 N 条
      if (this.isLargeData) {
        this.displayOptions = this.allOptions.slice(0, this.initDisplayCount);
        this.showDataTip = true;
      } else {
        this.displayOptions = [...this.allOptions];
      }
    },
    
    /**
     * 处理清空按钮点击
     */
    handleClear() {
      this.resetToAllData();
      this.$emit('clear');
    },
    
    /**
     * 搜索过滤方法（带防抖）
     */
    handleFilter(query) {
      this.currentQuery = query || '';
      
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      
      // 输入为空，恢复显示数据
      if (!query || query.trim() === '') {
        this.resetToAllData();
        return;
      }
      
      // 有搜索词时隐藏提示
      this.showDataTip = false;
      
      // 设置防抖（200ms）
      this.searchTimer = setTimeout(() => {
        this.performSearch(query);
      }, 200);
    },
    
    /**
     * 执行搜索
     */
    performSearch(query) {
      const startTime = performance.now();
      const searchKeyword = query.toLowerCase().trim();
      let results = [];
      
      // 使用索引搜索（大数据量）
      if (this.searchIndex) {
        for (let i = 0; i < this.searchIndex.length; i++) {
          const idx = this.searchIndex[i];
          let matched = false;
          
          // 标签匹配
          if (idx.label.indexOf(searchKeyword) !== -1) {
            matched = true;
          }
          // 拼音匹配
          else if (this.enablePinyinSearch && idx.pinyin.indexOf(searchKeyword) !== -1) {
            matched = true;
          }
          // 拼音首字母匹配
          else if (this.enableFirstLetterSearch && idx.firstLetters && idx.firstLetters.indexOf(searchKeyword) !== -1) {
            matched = true;
          }
          
          if (matched) {
            results.push(idx.item);
          }
        }
      } else {
        // 直接搜索（小数据量）
        for (let i = 0; i < this.allOptions.length; i++) {
          const item = this.allOptions[i];
          const label = (item[this.labelKey] || '').toLowerCase();
          const pinyin = (item[this.pinyinKey] || '').toLowerCase();
          let matched = false;
          
          if (label.indexOf(searchKeyword) !== -1) {
            matched = true;
          } else if (this.enablePinyinSearch && pinyin.indexOf(searchKeyword) !== -1) {
            matched = true;
          } else if (this.enableFirstLetterSearch && pinyin) {
            const words = pinyin.split(' ');
            const letters = [];
            for (let w = 0; w < words.length; w++) {
              if (words[w]) {
                letters.push(words[w].charAt(0));
              }
            }
            const firstLetters = letters.join('').toLowerCase();
            if (firstLetters.indexOf(searchKeyword) !== -1) {
              matched = true;
            }
          }
          
          if (matched) {
            results.push(item);
          }
        }
      }
      
      this.displayOptions = results;
      
      const endTime = performance.now();
    },
    
    /**
     * 下拉框显示/隐藏
     */
    handleVisibleChange(visible) {
      if (!visible) {
        // 关闭时，如果有搜索词但没有选中值，重置显示
        if (this.currentQuery && !this.selectedValue) {
          this.resetToAllData();
        }
      } else {
        // 打开时，如果没有搜索词，重置显示
        if (!this.currentQuery) {
          this.resetToAllData();
        }
      }
      this.$emit('visible-change', visible);
    },
    
    /**
     * 选择变化
     */
    handleChange(value) {
      // 获取选中的完整对象
      let selectedItem = null;
      for (let i = 0; i < this.allOptions.length; i++) {
        if (this.allOptions[i][this.valueKey] === value) {
          selectedItem = this.allOptions[i];
          break;
        }
      }
      
      this.$emit('change', value, selectedItem);
      this.$emit('on-change', value, selectedItem);
    },
    
    /**
     * 获取当前选中的项（对外暴露）
     */
    getSelectedItem(value) {
      const targetValue = value !== undefined ? value : this.selectedValue;
      for (let i = 0; i < this.allOptions.length; i++) {
        if (this.allOptions[i][this.valueKey] === targetValue) {
          return this.allOptions[i];
        }
      }
      return null;
    },
    
    /**
     * 重置筛选（对外暴露）
     */
    resetFilter() {
      this.resetToAllData();
    }
  }
}
</script>

<style scoped>
.search-empty,
.data-tip {
  text-align: center;
  padding: 10px;
  color: #909399;
  font-size: 12px;
}

.data-tip {
  color: #e6a23c;
}
</style>